import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { customerGroupSchema, type CustomerGroupFormData } from "../schemas/customerGroupSchema";
import type { CustomerGroup } from "../types";

interface CustomerGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CustomerGroupFormData) => void;
  editingGroup?: CustomerGroup | null;
  isLoading?: boolean;
}

export const CustomerGroupDialog = ({
  open,
  onOpenChange,
  onSubmit,
  editingGroup,
  isLoading,
}: CustomerGroupDialogProps) => {
  const form = useForm<CustomerGroupFormData>({
    resolver: zodResolver(customerGroupSchema),
    defaultValues: { descricao: "", isInativo: false },
  });

  useEffect(() => {
    if (open) {
      form.reset(
        editingGroup
          ? { descricao: editingGroup.descricao, isInativo: editingGroup.isInativo }
          : { descricao: "", isInativo: false }
      );
    }
  }, [open, editingGroup, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {editingGroup ? "Editar Grupo de Clientes" : "Novo Grupo de Clientes"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="descricao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Atacado, VIP, Padrão..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isInativo"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <Switch
                      id="ativo"
                      checked={!field.value}
                      onCheckedChange={(v) => field.onChange(!v)}
                    />
                  </FormControl>
                  <FormLabel htmlFor="ativo" className="!mt-0">Ativo</FormLabel>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Salvando..." : editingGroup ? "Atualizar" : "Criar Grupo"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
